/**
 * **拖放排序列表**（2026-10-30）。
 *
 * ## 为什么自己写、不引第三方库
 *
 * 本仓库刻意不引拖放库（图标都是 vendored 的）：一个"竖列表上下拖"的交互
 * 用指针事件约 100 行就能写完，而引一个百 KB 的库还要处理它与 React 18/19、
 * 触屏与无障碍的兼容。这里用 **Pointer Events**：鼠标与手指同一套代码，
 * 手机上也不需要长按（按下就进入拖动）。
 *
 * ## 交互约定
 *
 * - **只从左侧的手柄（⠿）开始拖**：整行都能拖会和"点开这一项"抢手势；
 * - 拖动中实时重排（本地预览），松手才回调 `onReorder(ids)` —— 由调用方决定存不存；
 * - `touch-action: none` 只加在手柄上，页面其余部分照常滚动（否则手机上滑不动）；
 * - 没有键盘拖放（屏幕阅读器用户用「上移 / 下移」按钮，它们一直在行右侧）。
 *
 * ## 无障碍
 *
 * 每行右手边有「上移 / 下移」按钮 —— 这是**唯一**保证"不用拖也能排"的路径，
 * 也是桌面端"拖不准"时的退路。拖放是增强，不是唯一入口。
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button';
import { IconArrowDown, IconArrowUp } from '../ui/Icons';
import { moveItem } from '../../lib/taskOrder';
import { cn } from '../../lib/utils';

export interface DragOrderItem {
  id: string;
  /** 主标题（孩子端看到的名字）。 */
  title: string;
  /** 副标题（上级目标 / 说明）。 */
  subtitle?: string;
}

export interface DragOrderListProps {
  items: readonly DragOrderItem[];
  /** 松手时回调（新的 id 顺序）。拖动过程中不会调用。 */
  onReorder: (ids: string[]) => void;
  disabled?: boolean;
}

export function DragOrderList({ items, onReorder, disabled = false }: DragOrderListProps) {
  const [order, setOrder] = useState<DragOrderItem[]>([...items]);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  /** 外部数据变了（换孩子 / 重新拉取）→ 用新顺序覆盖本地预览。 */
  const signature = items.map((item) => item.id).join(',');
  const lastSignatureRef = useRef(signature);
  useEffect(() => {
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;
    setOrder([...items]);
  }, [items, signature]);

  const listRef = useRef<HTMLUListElement | null>(null);
  /**
   * 拖动状态。
   *
   * `active=false` = **手指按下但还没到"长按"时长**：这段时间里手指一动（>8px）
   * 就当作"在滚动页面"，直接放弃 —— 这正是 2026-10-31 用户报的误拖的原因：
   * 手柄刚好在左手拇指上下滑的位置，一滑就被当成拖动。
   */
  const dragRef = useRef<{
    id: string;
    pointerId: number;
    startY: number;
    startX: number;
    fromIndex: number;
    active: boolean;
    /** 长按计时器（未激活前一直挂着；松手/移动/激活时清掉）。 */
    timer: ReturnType<typeof setTimeout> | null;
    /** 激活那刻的原始顺序（松手时用来判断有没有真变、以及越界回弹）。 */
    initialIds: string[];
    /** 手指按在行内的偏移（让浮动副本不从左上角开始跟手）。 */
    grabOffsetX: number;
    grabOffsetY: number;
    /** 解除 window 上那组 move/up/cancel 监听（未激活时为 null）。 */
    detach: (() => void) | null;
  } | null>(null);

  /** order 的镜像：window 监听里读到的必须是最新顺序（闭包会过期）。 */
  const orderRef = useRef(order);
  orderRef.current = order;

  /**
   * 长按多久才算"我要拖"（毫秒）。
   *
   * 220ms 是"比一次点击长、比滑动短"的折中：正常点击（<150ms）不会误触，
   * 想拖的人按住稍等一下就"拿起来"了。
   */
  const LONG_PRESS_MS = 220;

  /** 激活前允许的手指抖动（px）；超过就认为是在滚动。 */
  const MOVE_TOLERANCE_PX = 8;

  /** 指针按下的行高（拖动时用它换算"跨过了几行"）。 */
  const rowHeightRef = useRef(56);

  /**
   * **浮动副本**：激活后整行"离开列表"，用一个 `position: fixed` 的副本跟着手指走
   * （所以能在**整个屏幕**范围内挪动）。列表里原位留一个虚线空槽，
   * 松手时：在列表范围内 → 落到那个槽位；在列表外 → **回弹**（不保存）。
   */
  const [ghost, setGhost] = useState<{
    item: DragOrderItem;
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const commit = (next: DragOrderItem[]) => {
    setOrder(next);
    onReorder(next.map((item) => item.id));
  };

  const endDrag = () => {
    const drag = dragRef.current;
    if (drag?.timer) clearTimeout(drag.timer);
    drag?.detach?.();
    dragRef.current = null;
    setDraggingId(null);
    setGhost(null);
    /**
     * 恢复文字可选（见 `onPointerDown` 里那段说明）。
     * ⚠️ 放在这里而不是各个分支里：拖动无论以哪种方式结束都必须还原，
     * 否则整个页面会一直"选不中文字"。
     */
    document.body.style.userSelect = '';
    /** 解除"拖动期间禁止页面滚动"的那层保护（见 `onPointerDown` 的激活分支）。 */
    document.body.style.overflow = '';
  };

  /** 指针是否落在列表范围内（松手时判"可放置 / 回弹"）。 */
  const pointerOverList = (clientY: number): boolean => {
    const rect = listRef.current?.getBoundingClientRect();
    if (!rect) return false;
    /** 上下各给 12px 容差：手指压在边缘也算放进去。 */
    return clientY >= rect.top - 12 && clientY <= rect.bottom + 12;
  };

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>, id: string, index: number) => {
    if (disabled) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-row-id="${id}"]`);
    rowHeightRef.current = row?.offsetHeight ?? 56;
    const rect = row?.getBoundingClientRect();

    const startY = event.clientY;
    const startX = event.clientX;
    const pointerId = event.pointerId;
    const target = event.currentTarget;
    /** 手指在行内的相对位置：浮动副本按它偏移，看起来像"整块被拎起来"。 */
    const grabOffsetY = rect ? startY - rect.top : 0;
    const grabOffsetX = rect ? startX - rect.left : 0;

    /** 先只登记"按下了"，**不**激活拖动（等长按计时器）。 */
    dragRef.current = {
      id,
      pointerId,
      startY,
      startX,
      fromIndex: index,
      active: false,
      initialIds: [],
      grabOffsetX,
      grabOffsetY,
      detach: null,
      timer: setTimeout(() => {
        const drag = dragRef.current;
        if (drag === null || drag.pointerId !== pointerId) return;
        drag.active = true;
        drag.timer = null;
        drag.initialIds = order.map((item) => item.id);
        /** 激活时才捕获指针：在此之前页面照常滚动。 */
        (target as HTMLElement).setPointerCapture?.(pointerId);
        setDraggingId(id);
        /**
         * ⚠️ **拖动期间禁止选中文字 / 系统长按菜单**（2026-10-31 用户报的：
         * "按住它了，它浮起来了，但不到一秒钟又掉下去了，就像按不稳一样"）。
         *
         * 原因：手机浏览器在**长按约 500ms** 时会自己弹"选择 / 复制"菜单或开始选词，
         * 手势被系统接管 → 我们的指针流收到 `pointercancel` → 卡片"掉下去"。
         * 220ms 激活、500ms 被系统打断，这正好对上"不到一秒钟"。
         */
        document.body.style.userSelect = 'none';
        /**
         * **拖动期间不让页面跟着滚**（2026-10-31 第三次调整）。
         *
         * 手柄现在用的是 `touch-action: pan-y`（这样**从手柄上也能滑页面**，
         * 用户原话："上下滑动页面的时候滑不动了……就是在几个点那里"）。
         * 代价是浏览器随时可能接管竖滑并发 `pointercancel`。
         * 折中做法：长按激活之后（这时手指还基本没动、浏览器也还没开始滚）
         * 挂一个**非 passive** 的 `touchmove` 把默认行为挡掉 —— 既保留
         * "从手柄能滚"的平时手感，又保证"拿起之后只跟手、不滚页面"。
         */
        const blockScroll = (e: TouchEvent) => {
          if (dragRef.current?.active === true) e.preventDefault();
        };
        window.addEventListener('touchmove', blockScroll, { passive: false });
        const item = order.find((entry) => entry.id === id);
        if (item && rect) {
          setGhost({
            item,
            top: startY - grabOffsetY,
            left: startX - grabOffsetX,
            width: rect.width,
          });
        }

        /**
         * ⚠️ **在 window 上再挂一份 move/up**（2026-10-31 用户报的：
         * "拖动到别处，又想把它挪到屏幕外，这个时候就挪不出去了"）。
         *
         * 原因：手势一旦离开列表，若 `setPointerCapture` 没生效（部分浏览器 /
         * 被别的元素抢走），React 挂在 `<ul>` 上的监听就**收不到事件** ——
         * 浮动块停在原地，看起来像"拖不出去"。
         * 挂到 window 上就不依赖命中谁：手指在屏幕任何位置都继续跟手。
         */
        const onWinMove = (e: PointerEvent) => {
          if (e.pointerId !== pointerId) return;
          handleMove(e.clientX, e.clientY);
        };
        const onWinUp = (e: PointerEvent) => {
          if (e.pointerId !== pointerId) return;
          handleUp(e.clientY);
        };
        const onWinCancel = () => endDrag();
        window.addEventListener('pointermove', onWinMove);
        window.addEventListener('pointerup', onWinUp);
        window.addEventListener('pointercancel', onWinCancel);
        if (dragRef.current !== null) {
          dragRef.current.detach = () => {
            window.removeEventListener('pointermove', onWinMove);
            window.removeEventListener('pointerup', onWinUp);
            window.removeEventListener('pointercancel', onWinCancel);
            window.removeEventListener('touchmove', blockScroll);
          };
        }
      }, LONG_PRESS_MS),
    };
  };

  /** 拖动中的位移（React 事件与 window 监听**共用**这一份，避免两套逻辑漂移）。 */
  const handleMove = (clientX: number, clientY: number) => {
    const drag = dragRef.current;
    if (drag === null || !drag.active) return;
    const delta = clientY - drag.startY;

    /**
     * 浮动副本跟着手指走（**整个屏幕**范围内都能挪）。
     * 它 `pointer-events-none`，所以不影响列表上的命中判定。
     */
    setGhost((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            top: clientY - drag.grabOffsetY,
            left: clientX - drag.grabOffsetX,
          },
    );

    const steps = Math.round(delta / rowHeightRef.current);
    const target = Math.min(Math.max(drag.fromIndex + steps, 0), orderRef.current.length - 1);
    const currentIndex = orderRef.current.findIndex((item) => item.id === drag.id);
    if (currentIndex === -1 || target === currentIndex) return;
    setOrder(moveItem(orderRef.current, currentIndex, target));
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;

    /** ---- 还没到长按时长：一动就当成"在滚动页面"，放弃这次拖动 ---- */
    if (!drag.active) {
      if (Math.abs(event.clientY - drag.startY) > MOVE_TOLERANCE_PX) endDrag();
      return;
    }
    handleMove(event.clientX, event.clientY);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    handleUp(event.clientY);
  };

  const handleUp = (clientY: number) => {
    const drag = dragRef.current;
    if (drag === null) return;
    const wasActive = drag.active;
    const initialIds = drag.initialIds;
    const inside = pointerOverList(clientY);
    const currentIds = orderRef.current.map((item) => item.id);
    const changed = currentIds.join(',') !== initialIds.join(',');

    endDrag();
    /** 没激活（按一下就松手）= 普通点击，什么都不做。 */
    if (!wasActive) return;

    /**
     * 松手时的三种结果（2026-10-31 用户要求）：
     *  1. **在列表外松手** → 回弹到原来的位置，**不保存**；
     *  2. **在列表内但没挪动** → 什么都不做（不保存、不提示）；
     *  3. **在列表内且顺序变了** → 停在新位置并保存。
     */
    if (!inside || !changed) {
      /**
       * 回弹：按**原始顺序**恢复。
       * （`initialIds` 是激活那一刻的顺序；用它重建，比记住整个数组更稳 ——
       *  `order` 里只是同样几条、顺序被换过。）
       */
      setOrder((prev) => {
        const byId = new Map(prev.map((entry) => [entry.id, entry]));
        const restored = initialIds
          .map((id) => byId.get(id))
          .filter((entry): entry is DragOrderItem => entry !== undefined);
        return restored.length === prev.length ? restored : prev;
      });
      return;
    }
    onReorder(currentIds);
  };

  /**
   * 浏览器**打断**了这次拖动（`pointercancel`）。
   *
   * ⚠️ 2026-10-31 用户报的："稍微拖动一点，它就回到原位，然后提示保存成功"。
   * 根因是 `pointercancel` 被接到了 `onPointerUp` 上 —— 浏览器一旦接管手势
   * （滚动、系统手势）就会发 cancel，于是"取消"被当成了"松手"：摆回原位 + 存一次。
   *
   * 正确语义：**取消就是取消** —— 不改顺序、不落库、不提示。
   */
  const onPointerCancel = () => {
    endDrag();
  };

  const move = (index: number, delta: number) => {
    const target = Math.min(Math.max(index + delta, 0), order.length - 1);
    if (target === index) return;
    commit(moveItem(order, index, target));
  };

  return (
    <>
    <ul
      ref={listRef}
      className="select-none space-y-2"
      aria-label="拖动调整顺序"
      /*
        ⚠️ `select-none` + 禁掉长按菜单：手机长按会弹"选择 / 复制"并夺走手势，
        触摸手势一旦被系统接管，拖动就会中途"掉下去"（见 `onPointerDown` 的说明）。
      */
      style={{ WebkitTouchCallout: 'none' }}
      onContextMenu={(event) => event.preventDefault()}
      /*
        ⚠️ 指针事件在 `<ul>` 上也接一份：`setPointerCapture` 是在长按计时器里调的，
        个别浏览器/环境下会静默失败；那时手指离开手柄就收不到 move/up。
        事件冒泡到列表这一层兜住 —— `dragRef` 用 pointerId 认人，不会串行。
      */
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {order.map((item, index) => {
        const dragging = draggingId === item.id;
        return (
          <li
            key={item.id}
            data-row-id={item.id}
            /*
              **拖动中的原位是"虚线空槽"**（2026-10-31 用户要求："整个可以把这一块
              拖起来，在规定的范围内浮动起来一样"）：
              真正跟手的那一块是下面那个 `position: fixed` 的浮动副本，
              这里只留一个虚线框告诉人"松手会落在这儿"。
            */
            className={cn(
              'flex items-center gap-2 rounded-xl px-2 py-2 transition-colors duration-150',
              dragging
                ? /*
                    落点空槽（2026-10-31 用户："颜色有点浅，不太明显"）：
                    加深描边 + 淡蓝底 + 内阴影 + 一圈柔光，让人一眼看到"松手会落在这儿"。
                  */
                  'border-2 border-dashed border-brand-500 bg-brand-100/70 shadow-inner ring-2 ring-brand-200/70'
                : 'border border-slate-200 bg-white',
            )}
            /* 空槽里的内容半透明：仍能看出是哪一条，但不抢眼。 */
            style={dragging ? { opacity: 0.5 } : undefined}
          >
            <button
              type="button"
              aria-label={`按住拖动「${item.title}」调整顺序`}
              title="按住稍等，再上下拖动"
              disabled={disabled}
              onPointerDown={(event) => onPointerDown(event, item.id, index)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onContextMenu={(event) => event.preventDefault()}
              className="shrink-0 cursor-grab rounded-lg px-2 py-2 text-slate-400 active:cursor-grabbing disabled:cursor-not-allowed"
              /*
                ⚠️ 手柄用 `touch-action: pan-y`（2026-10-31 第三次调整，最终版）。
                
                三次的教训：
                · `none` → 从手柄上**滑不动页面**（用户："上下滑动页面的时候滑不动了……
                  就是在几个点那里"）；
                · `pan-y` 但没有任何拦截 → 浏览器接管竖滑、发 `pointercancel`
                  → "拿起后不到一秒掉下去 / 拖一点就弹回还提示保存成功"。
                
                最终做法：**放行滚动（`pan-y`）+ 长按激活后立刻挂非 passive 的
                `touchmove` 把默认行为挡掉**（见 `onPointerDown` 的激活分支）。
                效果：平时从手柄上滑 = 正常滚页面；长按拿起之后 = 只跟手、不滚页面。
              */
              style={{ touchAction: 'pan-y' }}
            >
              ⠿
            </button>

            <span className="w-6 shrink-0 text-center text-xs font-semibold text-slate-400">
              {index + 1}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block break-words text-sm font-semibold text-slate-900">
                {item.title}
              </span>
              {item.subtitle !== undefined && item.subtitle !== '' && (
                <span className="block break-words text-xs text-slate-500">{item.subtitle}</span>
              )}
            </span>

            <span className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                aria-label={`「${item.title}」上移`}
                disabled={disabled || index === 0}
                onClick={() => move(index, -1)}
                icon={<IconArrowUp size={14} />}
              >
                <span className="sr-only">上移</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`「${item.title}」下移`}
                disabled={disabled || index === order.length - 1}
                onClick={() => move(index, 1)}
                icon={<IconArrowDown size={14} />}
              >
                <span className="sr-only">下移</span>
              </Button>
            </span>
          </li>
        );
      })}
    </ul>

    {/*
      **浮动副本**：`position: fixed` + `pointer-events-none`，跟着手指在整个屏幕内移动。
      它只是"看起来被拎起来"的那一块；落点由列表里的虚线空槽决定。

      ⚠️ **必须 portal 到 `document.body`**（2026-10-31 用户报的："想把它挪到屏幕外，
      这个时候就挪不出去了"）：`position: fixed` 的包含块会被**任何带 transform /
      filter / contain 的祖先**改写 —— 那时它就只在这个卡片范围内"固定"，
      出了祖先边界就被裁掉，看起来像"拖不出去"。挂到 body 上就没有这个祖先链了。
    */}
    {ghost !== null &&
      createPortal(
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-[60] flex items-center gap-2 rounded-xl border-2 border-brand-400 bg-white px-2 py-2 shadow-2xl shadow-brand-900/25"
          style={{
            top: ghost.top,
            left: ghost.left,
            width: ghost.width,
            transform: 'scale(1.03)',
          }}
        >
          <span className="shrink-0 rounded-lg px-2 py-2 text-slate-400">⠿</span>
          <span className="min-w-0 flex-1">
            <span className="block break-words text-sm font-semibold text-slate-900">
              {ghost.item.title}
            </span>
            {ghost.item.subtitle !== undefined && ghost.item.subtitle !== '' && (
              <span className="block break-words text-xs text-slate-500">
                {ghost.item.subtitle}
              </span>
            )}
          </span>
        </div>,
        document.body,
      )}
  </>
  );
}
